"use client"

import React, { createContext, useContext, useState, useEffect } from 'react'

const MOBILE_BREAKPOINT = 768

export interface PlatformInfo {
  isMobile: boolean
  isDesktop: boolean
  isCapacitor: boolean
  isNative: boolean
  isIOS: boolean
  isAndroid: boolean
  isStandalone: boolean
  isWeb: boolean
  isLoaded: boolean
}

const defaultPlatformInfo: PlatformInfo = {
  isMobile: false,
  isDesktop: true,
  isCapacitor: false,
  isNative: false,
  isIOS: false,
  isAndroid: false,
  isStandalone: false,
  isWeb: true,
  isLoaded: false,
}

const PlatformContext = createContext<PlatformInfo>(defaultPlatformInfo)

export function PlatformProvider({ children }: { children: React.ReactNode }) {
  const [platform, setPlatform] = useState<PlatformInfo>(defaultPlatformInfo)

  useEffect(() => {
    const updatePlatform = () => {
      const win = window as any
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT
      
      // Platform detection
      const userAgent = navigator.userAgent || navigator.vendor || (win.opera as string)
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      const isAndroid = /Android/.test(userAgent)
      
      const capacitorPlatform = win.Capacitor?.platform
      const isCapacitor = !!(win.Capacitor && (capacitorPlatform === 'ios' || capacitorPlatform === 'android' || win.Capacitor?.isNative))
      
      const isWKWebView = isIOS && /Mobile/.test(userAgent) && !/Safari/.test(userAgent)
      
      let isStandalone = false
      try {
        isStandalone = window.matchMedia('(display-mode: standalone)').matches || win.navigator?.standalone === true
      } catch (e) {
        // Ignore error
      }
      
      const isNative = (isCapacitor && (
        typeof win.Capacitor?.isNativePlatform === 'function' 
          ? win.Capacitor.isNativePlatform() 
          : (win.Capacitor?.isNative ?? true)
      )) || (isWKWebView && !isStandalone) || userAgent.includes('SpotsToSurfApp')
      
      // Web is defined as NOT running in a native wrapper (Capacitor)
      // We are explicit: if it's not a native app or a standalone PWA, it's web
      const isWeb = !isNative && !isStandalone

      // Important: Delay isLoaded until we've had a chance to detect Capacitor on mobile devices
      // this prevents the "web flash" on app startup
      const isPotentiallyNative = isIOS || isAndroid
      const hasCheckedCapacitor = isCapacitor || !isPotentiallyNative
      
      setPlatform(prev => ({
        isMobile,
        isDesktop: !isMobile,
        isCapacitor,
        isNative,
        isIOS,
        isAndroid,
        isStandalone,
        isWeb,
        isLoaded: prev.isLoaded || hasCheckedCapacitor,
      }))
    }

    // Initial check
    updatePlatform()

    // If Capacitor wasn't found and we're on a mobile device, try again quickly
    // this helps prevent the web flash by catching the injection early
    const retryTimer = setTimeout(updatePlatform, 50)
    const retryTimer2 = setTimeout(updatePlatform, 300)
    const retryTimer3 = setTimeout(updatePlatform, 1000)
    
    // Safety fallback: if after 3 seconds we still haven't "loaded", force it
    // Increased to 3s to be extra sure Capacitor has had time to inject on slow devices/connections
    const finalFallback = setTimeout(() => {
      setPlatform(prev => {
        if (prev.isLoaded) return prev
        console.log("[PlatformProvider] Detection timeout reached, forcing loaded state")
        return { ...prev, isLoaded: true }
      })
    }, 3000)

    // Listen for resize to update isMobile/isDesktop
    window.addEventListener('resize', updatePlatform)
    
    // Listen for media query changes (PWA mode can technically change though rare)
    const mql = window.matchMedia('(display-mode: standalone)')
    try {
      mql.addEventListener('change', updatePlatform)
    } catch (e) {
      // Fallback for older browsers
      mql.addListener(updatePlatform)
    }

    return () => {
      clearTimeout(retryTimer)
      clearTimeout(retryTimer2)
      clearTimeout(retryTimer3)
      clearTimeout(finalFallback)
      window.removeEventListener('resize', updatePlatform)
      try {
        mql.removeEventListener('change', updatePlatform)
      } catch (e) {
        mql.removeListener(updatePlatform)
      }
    }
  }, [])

  return (
    <PlatformContext.Provider value={platform}>
      {children}
    </PlatformContext.Provider>
  )
}

export function usePlatformContext() {
  return useContext(PlatformContext)
}
